import { useQueryClient } from "@tanstack/react-query";

import usePartMutations from "../../../data/usePartMutations";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import { IPart, IPartImageType } from "../../../models/Part";
import { CameraIcon } from "@heroicons/react/24/outline";
import classNames from "../../../utils/classNames";
import { IPagedResponse } from "../../shared/Tables/PagedTable";
import { useMemo, useState } from "react";
import LoadingPulse from "../../shared/LoadingPulse";
import ClickToCopy from "../../shared/ClickToCopy";
import Modal from "../../../layouts/Modal";
import EbayLinkButton from "../../shared/Inputs/EbayLinkButton";


export default function UnlistedNpmUpdate({ part, onTable = true }: { part: IPart, onTable?: boolean }) {
  const { patchPart } = usePartMutations();
  const { saveHandlers } = useDefaultCRUDHandlers("Part Number");

  const [open, setOpen] = useState(false);

  const handleMpnUpdate = (mpn: string) => {
    const patchBody = {
      id: part.id,
      partNumber: mpn,
    };

    if (part.partNumber === mpn) return;
    if (!mpn) return;

    patch(patchBody);
  }

  const handleSupersededUpdate = (superseded: string) => {
    const patchBody = {
      id: part.id,
      supersededPartNumbers: superseded,
    };

    if (part.partNumber === superseded) return;
    if (!superseded) return;

    patch(patchBody);
  }

  const patch = (patchBody: any) => {
    patchPart.mutate(patchBody, {
      ...saveHandlers,
      onSuccess: () => {
        if (patchBody.partNumber) {
          part.partNumber = patchBody.partNumber;
        } else if (patchBody.supersededPartNumbers) {
          part.supersededPartNumbers = patchBody.supersededPartNumbers;
        }
        saveHandlers.onSuccess();
      },
    });
  }

  const mpnImage = useMemo(
    () => part.images?.find((image) => image.imageType === IPartImageType.MPN),
    [part.images]
  );

  if (patchPart.isLoading) {
    return <LoadingPulse />;
  }

  return (
    <>
      {/* Full-size modal */}
      <Modal open={open} setOpen={setOpen} width="max-w-7xl">
        <div>
          {mpnImage ? (
            <img className="w-full object-contain" src={mpnImage.url} />
          ) : (
            <CameraIcon className="w-16 text-gray-400 mx-auto" />
          )}
          <div className="p-1 mt-2">
            <label className="text-gray-700">MPN</label>
            <input
              key={part.id}
              defaultValue={part.partNumber ?? ""}
              className={classNames(
                onTable ? "w-full text-xs h-6 min-w-14" : "w-1/2",
                "border"
              )}
              type="text"
              onBlur={(e) => handleMpnUpdate(e.target.value)}
            />
          </div>

          <div className="p-1 mt-2">
            <label className="text-gray-700">Superseded Part Numbers</label>
            <input
              key={part.id}
              defaultValue={part.supersededPartNumbers ?? ""}
              className={classNames(
                onTable ? "w-full text-xs h-6 min-w-14" : "w-1/2",
                "border"
              )}
              type="text"
              onBlur={(e) => handleSupersededUpdate(e.target.value)}
            />
          </div>
        </div>
      </Modal>

      {/* Small inline display */}
      <div className="h-full flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div
          className={classNames(
            "flex flex-col justify-center relative",
            onTable && "items-center"
          )}
        >
          <span>
            {mpnImage ? (
              <img
                className="w-24 h-20 object-contain cursor-pointer hover:scale-110 transition-all"
                onClick={() => setOpen(true)}
                src={mpnImage.thumbnailUrl ?? mpnImage.url}
              />
            ) : (
              <CameraIcon className="w-10 text-gray-600" />
            )}
          </span>

          {!part.partNumber && (
            <p className="text-xs text-gray-500">No MPN</p>
          )}

          <div className="flex gap-1 mt-1 w-full">
            <div className="w-full">
              <label className="text-gray-700 text-xs">MPN</label>
              <input
                key={part.id}
                defaultValue={part.partNumber ?? ""}
                className={classNames(
                  onTable ? "w-full text-xs h-6" : "w-1/2",
                  "border p-1"
                )}
                type="text"
                onBlur={(e) => handleMpnUpdate(e.target.value)}
              />
            </div>
            <ClickToCopy value={part.partNumber} text={undefined} className="mt-4" />
          </div>

          {part.partNumber && (
            <EbayLinkButton query={part.partNumber} />
          )}

          <div className="flex gap-1 mt-1 w-full">
            <div className="w-full">
              <label className="text-gray-700 text-xs">Superseded Part Numbers</label>
              <input
                key={part.id}
                defaultValue={part.supersededPartNumbers ?? ""}
                className={classNames(
                  onTable ? "w-full text-xs h-6" : "w-1/2",
                  "border p-1"
                )}
                type="text"
                onBlur={(e) => handleSupersededUpdate(e.target.value)}
              />
            </div>
            <ClickToCopy value={part.supersededPartNumbers} text={undefined} className="mt-4" />
          </div>

          {part.oePartNumber && part.oePartNumber !== part.partNumber && (
            <>
              <div className="text-gray-700 mt-2 text-center">
                <p>OE PN:</p>
                <ClickToCopy text={part.oePartNumber} />
              </div>
              <EbayLinkButton query={part.oePartNumber} />
            </>
          )}
        </div>
      </div>
    </>
  );
}